import en from '../../../i18n/locales/en.json';
import fr from '../../../i18n/locales/fr.json';
import { TEMPLATE_LANGUAGES } from '../../../types/customEmailTemplate';
import type { TemplateLanguage } from '../../../types/customEmailTemplate';

export const MACROS = ['contactName', 'today', 'totalDue', 'transactionsTable'] as const;
export type Macro = (typeof MACROS)[number];

// Read straight from the locale files: the labels follow the template's language, never the interface's,
// and must resolve identically before i18next has initialised.
const MARKER_LABELS: Record<TemplateLanguage, Record<Macro, string>> = {
    FR: fr.customEmailTemplate.markers,
    EN: en.customEmailTemplate.markers,
};

// A single-brace marker, not part of a canonical `{{name}}` token.
const DISPLAY_MARKER = /(?<!\{)\{([^{}\n]+)\}(?!\})/g;

const canonicalToken = (macro: Macro): string => `{{${macro}}}`;

const normalize = (label: string): string => label.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

export const markerLabel = (macro: Macro, language: TemplateLanguage): string => MARKER_LABELS[language][macro];

/** The marker inserted into the text the user edits, e.g. `{Nom du contact}`. */
export const displayMarker = (macro: Macro, language: TemplateLanguage): string => `{${markerLabel(macro, language)}}`;

export const toDisplay = (text: string, language: TemplateLanguage): string =>
    MACROS.reduce((result, macro) => result.split(canonicalToken(macro)).join(displayMarker(macro, language)), text);

/** Recognizes the markers of either language, whatever their case or spacing. Unknown braces are left alone. */
export const toCanonical = (text: string): string => {
    const macroByLabel = new Map<string, Macro>();
    for (const language of TEMPLATE_LANGUAGES)
        for (const macro of MACROS) macroByLabel.set(normalize(markerLabel(macro, language)), macro);

    return text.replace(DISPLAY_MARKER, (marker, label: string) => {
        const macro = macroByLabel.get(normalize(label));
        return macro ? canonicalToken(macro) : marker;
    });
};

export interface CursorInsertion {
    value: string;
    cursor: number;
}

export const insertAtCursor = (value: string, selectionStart: number, selectionEnd: number, insertion: string): CursorInsertion => ({
    value: value.slice(0, selectionStart) + insertion + value.slice(selectionEnd),
    cursor: selectionStart + insertion.length,
});
